/* eslint-disable */
import { Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import cn from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';
import AppInfo from '../../Components/AppInfo/AppInfo.component';
import Banner from '../../Components/Banner/Banner.component';
import I18n from '../../Components/I18n/I18n.component';
import Login from '../../Components/Login/Login.component';
import Page from '../../Components/Page/Page.component';
import withEntries from '../../Contexts/Entries/WithEntries.context';
import authActionCreators from '../../State/Store/Auth/Auth.actions';
import userProfileActionCreators from '../../State/Store/UserProfile/UserProfile.actions';
import authCurrentSelector from '../../State/Store/Users/Selectors/UserCurrent.selector';
import envGet from '../../Support/Env/Get/EnvGet.support';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import './Login.page.i18n';
import LoginPagePropTypes from './Login.page.props';
import loginPageStyles from './Login.page.styles';

const FEATURE_TOGGLE_OKTA_SUPPORT = envGet({ key: `REACT_APP_BORAL_FEATURE_TOGGLE_OKTA_SUPPORT` }) === `true`;
const FEATURE_TOGGLE_OKTA_SIGN_IN_WIDGET = envGet({ key: `REACT_APP_BORAL_FEATURE_TOGGLE_OKTA_SIGN_IN_WIDGET` }) === `true`;

/**
 * LoginPageComponent
 * @export
 * @class LoginPageComponent
 * @extends {React.Component<LoginPageComponentComposedProps, LoginPageComponentState>}
 */
export class LoginPageComponent extends React.Component {
  preserveScrollRef = React.createRef();

  /**
   * The `props` to seed when any specific prop receives an `undefined` value. **NOT** a `null` value
   * @static
   * @type {Partial<LoginPageComponentProps>}
   * @memberof LoginPageComponent
   */
  static defaultProps = {};

  /**
   * The name of the Component to display when debugging
   * @static
   * @type {`LoginPage`}
   * @memberof LoginPageComponent
   */
  static displayName = `LoginPage`;

  /**
   * The values to use to apply runtime typechecking on the props
   * @static
   * @type {object}
   * @memberof LoginPageComponent
   */
  static propTypes = LoginPagePropTypes;

  /**
   * Data specific to the `<LoginPage />` instance
   * @type {LoginPageComponentState}
   * @memberof LoginPageComponent
   */
  state = {
    disableForm: false,
    errorMessage: ``,
    password: ``,
    showError: false,
    username: ``,
  };

  /**
   * Component did mount
   * @returns {void} void
   * @memberof LoginPageComponent
   */
  componentDidMount() {
    const { auth, history } = this.props;
    const {
      action,
      location: { state },
    } = history;
    if (auth === null && action === `REPLACE` && state != null) {
      this.setState({
        errorMessage: `LoginPage.UnauthorizedError`,
        showError: true,
      });
    }

    // Service workers
    const { appServiceWorker } = this.props;
    if (appServiceWorker) {
      appServiceWorker.onInstalled(() => {
        // console.log(`service worker installed`);
      });
      appServiceWorker.onUpdateFound(() => {
        console.log(`appServiceWorker update found, show plese refresh button`)
        this.setState({
          anchorOrigin: `top`,
          autoHideDuration: 24 * 3600 * 1000,
          hideAction: true,
          show: true,
        });
      });
    }
    return;
  }

  /**
   * Component will unmount
   * @returns {void} void
   * @memberof LoginPageComponent
   */
  componentWillUnmount() {
    return;
  }

  /**
   * Component did update
   * @param {prevProps} prevProps The `prevProps`
   * @returns {void} void
   * @memberof LoginPageComponent
   */
  componentDidUpdate(prevProps) {
    const { actions, auth } = this.props;
    if (auth !== prevProps.auth) {
      this.createUserState(auth.user, actions);
    }
    return;
  }

  /**
   * Creates the user state
   * @param {data} data The `data`
   * @param {actions} actions The `actions`
   * @returns {void} void
   * @memberof LoginPageComponent
   */
  createUserState = (data, actions) => {
    // Disptach action to save user profile in state
    actions.createUserProfile({ profile: data });
    return;
  };

  /**
   * For handling the clsoe snankbar message
   * @memberof HomePage
   * @type {`HomePage`}
   * @param {Object} event  the event
   * @param {Object} reason  the reason
   * @returns {void} void
   */
  onHandleCloseMessage = (event, reason) => {
    this.setState({ autoHideDuration: 8000, message: ``, show: false });
    return;
  };

  /**
   * Handle any form element changes
   * @param {string} key The `key` in the state to update
   * @returns {EventFunction} A bound listener to be consumed by a React component's `on*` handlers
   * @memberof LoginPageComponent
   */
  handleChange = (key) => (event) => {
    this.setState({
      [key]: event.target.value,
    });
    return;
  };

  /**
   * Clears the error message
   * @returns {void} void
   * @memberof LoginPageComponent
   */
  clearError = () => {
    this.setState({
      errorMessage: ``,
      showError: false,
    });
    return;
  };

  /**
   * Attempt to log in the user
   * @returns {Promise<void>} void
   * @memberof LoginPageComponent
   */
  handleLogin = async () => {
    const { actions, entry, history } = this.props;
    const { password, username } = this.state;
    this.setState({
      disableForm: true,
    });
    const result = await actions.authLoginWithUsernameAndPassword({ password, username });
    if (result.error === null) {
      history.push(entry.postLoginLink);
    } else {
      this.setState({
        disableForm: false,
        errorMessage: `LoginPage.LoginError`,
        showError: true,
      });
    }
    return;
  };

  /**
   * Render the custom login
   * @returns {React.Component} The rendered custom login
   * @memberof LoginPageComponent
   */
  renderCustomLogin() {
    const { classes, entry, theme } = this.props;
    const { disableForm, errorMessage, showError, password, username } = this.state;
    return (
      <form className={classes.loginBox} ref={this.preserveScrollRef}>
        <Paper className={classes.paper} elevation={0}>
          <I18n className={classes.pageTitle} data={{ name: entry.name }} variant="h5">
            LoginPage.PageTitle
          </I18n>
          <TextField
            disabled={disableForm}
            fullWidth
            label={<I18n>LoginPage.Username</I18n>}
            margin="normal"
            onChange={this.handleChange(`username`)}
            onClick={this.clearError}
            type="text"
            value={username}
          />
          <TextField
            disabled={disableForm}
            fullWidth
            label={<I18n>LoginPage.Password</I18n>}
            margin="normal"
            onChange={this.handleChange(`password`)}
            onClick={this.clearError}
            type="password"
            value={password}
          />
          <Button className={cn(classes.submitButton)} color="primary" disabled={disableForm} onClick={this.handleLogin} type="submit" variant="contained">
            <I18n>LoginPage.Login</I18n>
            {disableForm && <CircularProgress className={cn(classes.progress)} color="inherit" size={theme.spacing(1) * 2} />}
          </Button>
        </Paper>
        <Snackbar
          anchorOrigin={{
            horizontal: `center`,
            vertical: `bottom`,
          }}
          autoHideDuration={6000}
          ContentProps={{
            [`aria-describedby`]: `message-id`,
          }}
          onClose={this.clearError}
          open={showError}
        >
          <SnackbarContent
            message={
              errorMessage && (
                <I18n align="center" className={classes.error} variant="subtitle1">
                  {errorMessage}
                </I18n>
              )
            }
          />
        </Snackbar>
      </form>
    );
  }

  /**
   * Render the signin widget
   * @returns {React.Component} The rendered signin widget
   * @memberof LoginPageComponent
   */
  renderSignInWidget() {
    return <Login />;
  }

  /**
   * Render the component
   * @returns {React.Component} The rendered `<LoginPage />`
   * @memberof LoginPageComponent
   */
  render() {
    const { auth, entry } = this.props;
    // If Okta Sign In Widget is used, there's a lag time between authentication
    // and redirect to actual postLoginLink. So while auth === null while authentication
    // is happening, redirects to and from login will happen.
    // Only do the redirect if Okta Sign In Widget is not used.
    if (auth !== null && !FEATURE_TOGGLE_OKTA_SIGN_IN_WIDGET) {
      return <Redirect to={entry.postLoginLink} />;
    }
    const shouldShowSignInWidget = FEATURE_TOGGLE_OKTA_SUPPORT && FEATURE_TOGGLE_OKTA_SIGN_IN_WIDGET;
    const { anchorOrigin, autoHideDuration, hideAction, message, show } = this.state;
    return (
      <React.Fragment>
        <AppInfo />
        <Page
          alwaysCenter
          heading="LoginPage.PageHeading"
          headingData={{ name: entry.name }}
          hideNavbar={shouldShowSignInWidget}
          hideSidebar
          padding="none"
          title="LoginPage.PageTitle"
          titleData={{ name: entry.name }}
        >
          <Banner />
          {shouldShowSignInWidget ? this.renderSignInWidget() : this.renderCustomLogin()}
          <Snackbar
            action={[
              hideAction || (
                <IconButton aria-label="Close" color="inherit" key="close" onClick={this.onHandleCloseMessage}>
                  <CloseIcon />
                </IconButton>
              ),
            ]}
            anchorOrigin={{
              horizontal: `center`,
              vertical: anchorOrigin || `bottom`,
            }}
            autoHideDuration={autoHideDuration}
            message={
              <Grid container alignItems="center" spacing={1}>
                <Grid item>There is a new update</Grid>
                <Grid item>
                  <Button color="primary" size="small" variant="contained" onClick={() => window.location.reload()}>
                    please refresh
                  </Button>
                </Grid>
              </Grid>
            }
            onClose={hideAction ? () => {} : this.onHandleCloseMessage}
            open={show}
          />
        </Page>
      </React.Fragment>
    );
  }
}

/** @type {React.ComponentClass<LoginPageComponentProps>} */
const LoginPage = compose(
  withEntries,
  withRouter,
  withStyles(themeCreateInjectableStyles(loginPageStyles), { withTheme: true }),
  connect(
    (state) => ({
      auth: authCurrentSelector(state),
    }),
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...authActionCreators,
          ...userProfileActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(LoginPageComponent);

export default LoginPage;
